import React, { useEffect, useRef } from "react";
import Logo from "../components/Logo";
import RealityNav from "../components/reality/RealityNav";
import RealityHero from "../components/reality/RealityHero";
import EpisodeCard from "../components/reality/EpisodeCard";
import CastGrid from "../components/reality/CastGrid";
import TrailerSection from "../components/reality/TrailerSection";
import PreRegisterForm from "../components/reality/PreRegisterForm";
import { useContent } from "../lib/ContentContext";

function Marquee({ text }) {
  const items = String(text || "").split("·").map((s) => s.trim()).filter(Boolean);
  const Row = () => (
    <div className="flex items-center gap-12 pr-12">
      {items.map((w, i) => (
        <span key={i} className="condensed text-[28px] uppercase tracking-[0.16em] text-[#F1EDE6]/70 flex items-center gap-12">
          {w}<span className="text-[color:var(--alta-red)]">✦</span>
        </span>
      ))}
    </div>
  );
  return (
    <div data-testid="reality-marquee" className="relative overflow-hidden marquee-dark py-4">
      <div className="marquee-track"><Row /><Row /></div>
    </div>
  );
}

function Manifesto({ t }) {
  return (
    <section data-testid="reality-manifesto" className="relative py-28 md:py-40 border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <div className="mono text-[10px] uppercase tracking-[0.32em] text-[color:var(--alta-red-soft)]">{t.m_eyebrow}</div>
          <h2 className="mt-6 condensed text-[64px] md:text-[110px] text-[#F1EDE6] whitespace-pre-line">{t.m_title}</h2>
          <p className="mt-8 text-[17px] md:text-[19px] leading-[1.75] text-[#F1EDE6]/70 font-light max-w-[62ch]">{t.m_body}</p>
        </div>
        <div className="lg:col-span-5 lg:pt-16">
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-10 lg:gap-12">
            {[1, 2, 3].map((n) => (
              <div key={n} data-testid={`reality-stat-${n}`} className="border-l border-[color:var(--alta-red)]/60 pl-6">
                <div className="condensed text-[64px] md:text-[96px] leading-none text-[#F1EDE6]">{t[`m_stat${n}_k`]}</div>
                <div className="mt-2 mono text-[10px] uppercase tracking-[0.26em] text-[#F1EDE6]/55">{t[`m_stat${n}_v`]}</div>
              </div>
            ))}
          </div>
          <blockquote className="mt-16 border-t border-white/10 pt-8">
            <p className="serif italic text-[26px] md:text-[32px] leading-tight text-[#F1EDE6]/90">{t.m_quote}</p>
            <footer className="mt-4 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red-soft)]">— {t.m_quote_by}</footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

function Episodes({ t, refEl }) {
  const episodes = Array.isArray(t.episodes) ? t.episodes : [];
  return (
    <section ref={refEl} id="episodios" data-testid="reality-episodes" className="relative py-28 md:py-40 border-b border-white/10 scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-8">
            <div className="mono text-[10px] uppercase tracking-[0.32em] text-[color:var(--alta-red-soft)]">{t.ep_eyebrow}</div>
            <h2 className="mt-6 condensed text-[64px] md:text-[110px] text-[#F1EDE6] whitespace-pre-line">{t.ep_title}</h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[15px] leading-[1.75] text-[#F1EDE6]/65 font-light">{t.ep_body}</p>
            <a href={t.channel_url} target="_blank" rel="noreferrer" data-testid="reality-channel-link"
               className="mt-6 inline-block mono text-[11px] uppercase tracking-[0.26em] text-[#F1EDE6] hover:text-[color:var(--alta-red-soft)] border-b border-[#F1EDE6]/30 hover:border-[color:var(--alta-red-soft)] pb-1 transition-colors">
              {t.channel_label}
            </a>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {episodes.map((ep, i) => <EpisodeCard key={ep.id || i} ep={ep} index={i} t={t} featured={i === 0} />)}
        </div>
      </div>
    </section>
  );
}

function Provocation({ t }) {
  return (
    <section data-testid="reality-provocation" className="relative bg-[color:var(--alta-red)] text-[#F1EDE6] py-28 md:py-40 overflow-hidden">
      <div className="grain-static opacity-30" />
      <div className="absolute -right-24 -bottom-24 opacity-10 pointer-events-none hidden lg:block">
        <Logo variant="light" size={640} testid="reality-provocation-mark" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mono text-[10px] uppercase tracking-[0.32em] text-[#F1EDE6]/80">{t.p_eyebrow}</div>
        <h2 className="mt-6 condensed text-[56px] md:text-[104px] whitespace-pre-line max-w-[18ch]">{t.p_title}</h2>
        <p className="mt-8 text-[17px] md:text-[20px] leading-[1.7] font-light max-w-[64ch] text-[#F1EDE6]/90">{t.p_body}</p>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 border-t border-[#F1EDE6]/25 pt-12">
          {[1, 2, 3].map((n) => (
            <div key={n} data-testid={`reality-pillar-${n}`}>
              <div className="mono text-[11px] tracking-[0.3em] text-[#F1EDE6]/60">{t[`p${n}_k`]}</div>
              <div className="mt-3 condensed text-[34px] md:text-[40px] tracking-[0.04em]">{t[`p${n}_t`]}</div>
              <p className="mt-3 text-[14px] leading-[1.7] text-[#F1EDE6]/85 font-light">{t[`p${n}_d`]}</p>
            </div>
          ))}
        </div>
        <a href={t.premiere_url || t.channel_url} target="_blank" rel="noreferrer" data-testid="reality-provocation-cta" className="mt-16 btn-outline-light">
          {t.p_cta} →
        </a>
      </div>
    </section>
  );
}

function PreRegister({ t, refEl, onWatch }) {
  return (
    <section ref={refEl} id="pre-inscricao" data-testid="reality-preregister" className="relative py-28 md:py-40 border-b border-white/10 scroll-mt-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="mono text-[10px] uppercase tracking-[0.32em] text-[color:var(--alta-red-soft)]">{t.f_eyebrow}</div>
          <h2 className="mt-6 condensed text-[64px] md:text-[96px] text-[#F1EDE6]">
            {t.f_title_pre}<span className="text-[color:var(--alta-red-soft)]">{t.f_title_accent}</span>
          </h2>
          <p className="mt-8 text-[16px] leading-[1.75] text-[#F1EDE6]/70 font-light max-w-[50ch]">{t.f_body}</p>
          <div className="mt-8 inline-flex items-center gap-3 border border-[#F1EDE6]/20 px-4 py-2 mono text-[10px] uppercase tracking-[0.26em] text-[#F1EDE6]/70">
            <span className="rec-dot" /> {t.f_scarcity}
          </div>
        </div>
        <div className="lg:col-span-7 lg:pt-4">
          <PreRegisterForm t={t} onWatch={onWatch} />
        </div>
      </div>
    </section>
  );
}

function Closing({ t, onJoin }) {
  return (
    <footer data-testid="reality-footer" className="relative py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <h2 className="condensed text-[72px] md:text-[150px] lg:text-[190px] text-[#F1EDE6]">
          {t.c_line_pre}<span className="text-[color:var(--alta-red-soft)]">{t.c_line_accent}</span>
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a href={t.premiere_url || t.channel_url} target="_blank" rel="noreferrer" data-testid="reality-footer-cta" className="btn-red-primary">{t.c_cta} →</a>
          <button type="button" onClick={onJoin} data-testid="reality-footer-prelist" className="btn-outline-light">{t.f_submit}</button>
        </div>
        <div className="mt-24 flex flex-col md:flex-row md:items-center justify-between gap-6 border-t border-white/10 pt-8">
          <div className="flex items-center gap-4">
            <Logo variant="light" size={36} testid="reality-footer-logo" />
            <span className="mono text-[10px] uppercase tracking-[0.28em] text-[#F1EDE6]/50">{t.footer_line}</span>
          </div>
          <div className="flex items-center">
            <a href="/admin/login" data-testid="reality-footer-admin-link"
               className="mono text-[9px] uppercase tracking-[0.32em] text-[#F1EDE6]/25 hover:text-[color:var(--alta-red-soft)] transition-colors">
              · admin ·
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Reality() {
  const { content } = useContent();
  const t = content.reality;
  const epRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "Casa Alta · O Reality — Alta Agency";
  }, []);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const onWatch = () => scrollTo(epRef);
  const onJoin = () => scrollTo(formRef);
  const trailerRef = useRef(null);
  const onTrailer = () => scrollTo(trailerRef);

  return (
    <div className="reality-page min-h-screen" data-testid="reality-page">
      <RealityNav t={t} onWatch={onWatch} onTrailer={t.hero_video ? onTrailer : undefined} />
      <RealityHero t={t} onWatch={onWatch} onTrailer={onTrailer} />
      <TrailerSection t={t} refEl={trailerRef} />
      <Marquee text={t.marquee} />
      <Manifesto t={t} />
      <CastGrid t={t} />
      <Episodes t={t} refEl={epRef} />
      <Provocation t={t} />
      <PreRegister t={t} refEl={formRef} onWatch={onWatch} />
      <Closing t={t} onJoin={onJoin} />
    </div>
  );
}
